<template>
  <div>
    <h3 class="text-bold">Update Order</h3>
    <!-- Order Header -->
    <div class="row">
      <div id="order-reference-container" class="col-md-8">
        <h5 class="text-bold">Order Reference #</h5>
        <h1 class="text-bold">
          {{ order.order_reference }}
        </h1>
      </div>
      <div
        v-if="this.order.supplier"
        id="supplier-container"
        class="col-md-4 text-right"
      >
        <h5 class="text-bold">Supplier</h5>
        <h3 class="text-bold">
          {{ order.supplier.name }} ({{ order.supplier.code }})
        </h3>
      </div>
    </div>
    <!-- Order Header -->

    <!-- Fabric Notes -->
    <div id="fabric-notes-panel" class="grid shadow bg-white mt-10">
      <div class="grid-title">
        <h4 class="text-black text-bold">
          <i
            v-if="order.fabric_order_notes.length === 0"
            class="fa text-success fa-circle-o"
          ></i>
          <i
            v-if="order.fabric_order_notes.length > 0"
            class="fa text-success fa-check-circle"
          ></i>
          Fabric Notes
          <span
            @click="panels.fabricNotesPanel.opened = false"
            v-if="panels.fabricNotesPanel.opened"
            class="btn btn-primary btn-small pull-right"
          >
            <span class="fa fa-minus-circle"></span>
          </span>

          <span
            @click="panels.fabricNotesPanel.opened = true"
            v-if="!panels.fabricNotesPanel.opened"
            class="btn btn-primary btn-small pull-right"
          >
            <span class="fa fa-plus-circle"></span>
          </span>
        </h4>
      </div>
      <div class="grid-body" v-if="panels.fabricNotesPanel.opened">
        <OrderNotes
          :notes="order.fabric_order_notes"
          :orderNumber="order.id"
          :isReadOnlyUser="isReadOnlyUser"
          createRoute="v2/fabrics/orders/create/fabric_order_note"
          updateRoute="v2/fabrics/orders/update/fabric_order_note"
          deleteRoute="v2/fabrics/orders/delete/fabric_order_note"
          title="Fabric Order Infos"
          orderType="fabric"
          @update:notes="order.fabric_order_notes = $event"
          :currentUser="this.$auth.user()"
        />
      </div>
    </div>

    <!-- Invoice Details -->
    <div id="invoice-details-panel" class="grid shadow bg-white mt-10">
      <div class="grid-title">
        <h4 class="text-black text-bold">
          <i
            v-if="
              ![
                order.invoice_date,
                order.invoice_reference,
                order.invoice_net,
                order.invoice_vat,
                order.invoice_total,
                order.invoice_currency,
              ].every(Boolean)
            "
            class="fa text-success fa-circle-o"
          ></i>
          <i
            v-if="
              [
                order.invoice_date,
                order.invoice_reference,
                order.invoice_net,
                order.invoice_vat,
                order.invoice_total,
                order.invoice_currency,
              ].every(Boolean)
            "
            class="fa text-success fa-check-circle"
          ></i>
          Invoice Details
          <span
            @click="panels.invoiceDetailsPanel.opened = false"
            v-if="panels.invoiceDetailsPanel.opened"
            class="btn btn-primary btn-small pull-right"
          >
            <span class="fa fa-minus-circle"></span>
          </span>

          <span
            @click="panels.invoiceDetailsPanel.opened = true"
            v-if="!panels.invoiceDetailsPanel.opened"
            class="btn btn-primary btn-small pull-right"
          >
            <span class="fa fa-plus-circle"></span>
          </span>
        </h4>
      </div>
      <div class="grid-body" v-if="panels.invoiceDetailsPanel.opened">
        <table class="table">
          <thead>
            <tr>
              <th>Invoice Date</th>
              <th>Invoice Ref</th>
              <th>Internal Voucher Num</th>
              <th>Invoice Net</th>
              <th>VAT / Fees</th>
              <th>Invoice Total</th>
              <th>Currency</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <datepicker
                  v-model="order.invoice_date_picked"
                  @input="selectInvoiceDate()"
                  placeholder="Select Date"
                  :input-class="{
                    'has-error': errors.invoice_date,
                    'form-control': true,
                  }"
                  bootstrap-styling
                ></datepicker>
                <small v-if="errors.invoice_date" class="form-text text-danger">
                  {{ errors.invoice_date.join(" ") }}
                </small>
              </td>
              <td>
                <input
                  type="text"
                  v-bind:class="{ 'has-error': errors.invoice_reference }"
                  class="form-control"
                  v-model="order.invoice_reference"
                  placeholder="Invoice Reference"
                />
                <small
                  v-if="errors.invoice_reference"
                  class="form-text text-danger"
                >
                  {{ errors.invoice_reference.join(" ") }}
                </small>
              </td>
              <td>
                <input
                  type="text"
                  v-bind:class="{ 'has-error': errors.internal_voucher_number }"
                  class="form-control"
                  v-model="order.internal_voucher_number"
                  placeholder="Internal Voucher Number"
                />
                <small
                  v-if="errors.internal_voucher_number"
                  class="form-text text-danger"
                >
                  {{ errors.internal_voucher_number.join(" ") }}
                </small>
              </td>
              <td>
                <money
                  v-model="order.invoice_net"
                  v-bind:class="{ 'has-error': errors.invoice_net }"
                  class="form-control"
                  v-bind="fieldValues.money"
                ></money>
                <small v-if="errors.invoice_net" class="form-text text-danger">
                  {{ errors.invoice_net.join(" ") }}
                </small>
              </td>
              <td>
                <money
                  v-model="order.invoice_vat"
                  v-bind:class="{ 'has-error': errors.invoice_vat }"
                  class="form-control"
                  v-bind="fieldValues.money"
                ></money>
                <small v-if="errors.invoice_vat" class="form-text text-danger">
                  {{ errors.invoice_vat.join(" ") }}
                </small>
              </td>
              <td>
                <money
                  v-bind:class="{ 'has-error': errors.invoice_total }"
                  class="form-control"
                  v-model.lazy="order.invoice_total"
                  v-bind="fieldValues.money"
                  placeholder="Invoice Total"
                ></money>
                <small
                  v-if="errors.invoice_total"
                  class="form-text text-danger"
                >
                  {{ errors.invoice_total.join(" ") }}
                </small>
              </td>
              <td>
                <v-select
                  :options="['THB', 'USD']"
                  v-model="order.invoice_currency"
                  placeholder="Choose Currency"
                />
                <small v-if="errors.currency" class="form-text text-danger">
                  {{ errors.currency.join(" ") }}
                </small>
              </td>
            </tr>
          </tbody>
        </table>
        <input
          type="submit"
          @click="saveInvoiceData()"
          :disabled="this.loading"
          class="btn btn-block btn-primary mb-20"
          value="Save Invoice Details"
          v-if="!isReadOnlyUser"
        />
      </div>
    </div>
    <!-- Invoice Details -->

    <!-- Payment Details -->
    <div id="order-table" class="grid shadow bg-white mt-10">
      <div class="grid-title">
        <h4 class="text-black text-bold">
          <i
            v-if="panels.paymentDetailsPanel.results.length == 0"
            class="fa text-success fa-circle-o"
          ></i>
          <i
            v-if="panels.paymentDetailsPanel.results.length > 0"
            class="fa text-success fa-check-circle"
          ></i>

          Payment Details
          <span
            @click="panels.paymentDetailsPanel.opened = false"
            v-if="panels.paymentDetailsPanel.opened"
            class="btn btn-primary btn-small pull-right"
          >
            <span class="fa fa-minus-circle"></span>
          </span>

          <span
            @click="panels.paymentDetailsPanel.opened = true"
            v-if="!panels.paymentDetailsPanel.opened"
            class="btn btn-primary btn-small pull-right"
          >
            <span class="fa fa-plus-circle"></span>
          </span>
        </h4>
      </div>
      <div class="grid-body" v-if="panels.paymentDetailsPanel.opened">
        <div id="order-payment-status-container">
          <div class="row">
            <div class="col-sm-9">
              <h5 class="semi-bold">Change Order Status</h5>
              <small>
                <span class="fa fa-info-circle"></span> Order Payment Status is
                set automatically but you can use this control to set the status
                manually.
              </small>
            </div>
            <div class="col-sm-3 text-right">
              <button
                class="btn btn-default"
                v-bind:class="{
                  'btn-primary': order.payment_status === 'open',
                }"
                @click="changeOrderPaymentStatus('open')"
              >
                Open
              </button>
              <button
                class="btn btn-default"
                v-bind:class="{
                  'btn-primary': order.payment_status === 'deposit_paid',
                }"
                @click="changeOrderPaymentStatus('deposit_paid')"
              >
                Deposit Paid
              </button>
              <button
                class="btn btn-default"
                v-bind:class="{
                  'btn-primary': order.payment_status === 'fully_paid',
                }"
                @click="changeOrderPaymentStatus('fully_paid')"
              >
                Fully Paid
              </button>
            </div>
          </div>
        </div>

        <table class="table table-condensed p-0">
          <thead>
            <tr>
              <th class="col-sm-1 text-center">Payment Date</th>
              <th class="col-sm-1 text-center">Type</th>
              <th class="col-sm-1 text-center">Paid Amount</th>
              <th class="col-sm-1 text-center">Invoiced Amount</th>
              <th class="col-sm-1 text-center">Fees &amp; Other</th>
              <th class="col-sm-1 text-center">Currency</th>
              <th class="col-sm-1 text-center">Exchange Rate</th>
              <th class="col-sm-2 text-right">
                <button
                  class="btn btn-info"
                  v-if="!isReadOnlyUser"
                  @click="
                    panels.paymentDetailsPanel.addPaymentDialogOpened =
                      !panels.paymentDetailsPanel.addPaymentDialogOpened
                  "
                >
                  <i
                    class="fa fa-plus-circle"
                    v-if="!panels.paymentDetailsPanel.addPaymentDialogOpened"
                  ></i>
                  <i
                    class="fa fa-minus-circle"
                    v-if="panels.paymentDetailsPanel.addPaymentDialogOpened"
                  ></i>
                  Add Payment
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <template
              v-for="(payment, index) in panels.paymentDetailsPanel.results"
            >
              <tr :key="index">
                <td class="text-center">
                  {{
                    payment.handled_date !== null ? payment.handled_date : "-"
                  }}
                </td>
                <td class="text-center">{{ payment.type }}</td>
                <td class="text-center">{{ payment.amount_paid }}</td>
                <td class="text-center">{{ payment.amount_invoiced }}</td>
                <td class="text-center">{{ payment.amount_other }}</td>
                <td class="text-center">
                  <span
                    v-if="payment.currency_invoiced === payment.currency_paid"
                  >
                    {{ payment.currency_invoiced }}
                  </span>
                  <span
                    v-if="payment.currency_invoiced !== payment.currency_paid"
                  >
                    {{ payment.currency_invoiced }}:{{ payment.currency_paid }}
                  </span>
                </td>
                <td class="text-center">
                  {{
                    payment.exchange_rate !== null ? payment.exchange_rate : "-"
                  }}
                </td>
                <td class="text-right">
                  <button
                    class="btn btn-danger"
                    @click="deletePayment(payment.id)"
                    v-if="!isReadOnlyUser"
                  >
                    <i class="fa fa-trash" aria-hidden="true"></i>
                  </button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <div
          id="add-payment-form-container"
          v-if="panels.paymentDetailsPanel.addPaymentDialogOpened"
        >
          <h5 class="semi-bold mb-20">
            <span class="fa fa-credit-card"></span> Add Payment
          </h5>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group row">
                <label class="col-sm-2 col-form-label">Type</label>
                <div class="col-sm-10">
                  <v-select
                    :options="['deposit', 'outstanding']"
                    v-model="panels.paymentDetailsPanel.data.type"
                    placeholder="Choose Type of Payment"
                  />
                  <small
                    v-if="panels.paymentDetailsPanel.errors.type"
                    class="form-text text-danger"
                  >
                    {{ panels.paymentDetailsPanel.errors.type.join(" ") }}
                  </small>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-sm-2 col-form-label">Invoice Amount</label>
                <div class="col-sm-10">
                  <money
                    v-model="panels.paymentDetailsPanel.data.amount_invoiced"
                    placeholder="Enter the amount that was invoiced"
                    v-bind:class="{
                      'has-error':
                        panels.paymentDetailsPanel.errors.amount_invoiced,
                    }"
                    class="form-control"
                    v-bind="fieldValues.money"
                  >
                  </money>
                  <small
                    v-if="panels.paymentDetailsPanel.errors.amount_invoiced"
                    class="form-text text-danger"
                  >
                    {{
                      panels.paymentDetailsPanel.errors.amount_invoiced.join(
                        " "
                      )
                    }}
                  </small>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-sm-2 col-form-label"
                  >Fees / Taxes / Other</label
                >
                <div class="col-sm-10">
                  <money
                    v-model="panels.paymentDetailsPanel.data.amount_other"
                    placeholder="Enter the amount that was paid in fees, taxes and other expenses"
                    v-bind:class="{
                      'has-error':
                        panels.paymentDetailsPanel.errors.amount_other,
                    }"
                    class="form-control"
                    v-bind="fieldValues.money"
                  >
                  </money>
                  <small
                    v-if="panels.paymentDetailsPanel.errors.amount_other"
                    class="form-text text-danger"
                  >
                    {{
                      panels.paymentDetailsPanel.errors.amount_other.join(" ")
                    }}
                  </small>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-sm-2 col-form-label">Invoice Currency</label>
                <div class="col-sm-10">
                  <v-select
                    :options="['USD', 'THB']"
                    v-model="panels.paymentDetailsPanel.data.currency_invoiced"
                    placeholder="Choose Invoice Currency"
                  />
                  <small
                    v-if="panels.paymentDetailsPanel.errors.currency_invoiced"
                    class="form-text text-danger"
                  >
                    {{
                      panels.paymentDetailsPanel.errors.currency_invoiced.join(
                        " "
                      )
                    }}
                  </small>
                </div>
              </div>
              <div class="form-group row">
                <label class="col-sm-2 col-form-label">Payment Currency</label>
                <div class="col-sm-10">
                  <v-select
                    :options="['USD', 'THB']"
                    v-model="panels.paymentDetailsPanel.data.currency_paid"
                    placeholder="Choose Payment Currency"
                  />
                  <small
                    v-if="panels.paymentDetailsPanel.errors.currency_paid"
                    class="form-text text-danger"
                  >
                    {{
                      panels.paymentDetailsPanel.errors.currency_paid.join(" ")
                    }}
                  </small>
                </div>
              </div>
              <div
                class="form-group row"
                v-if="
                  panels.paymentDetailsPanel.data.currency_invoiced !==
                  panels.paymentDetailsPanel.data.currency_paid
                "
              >
                <label class="col-sm-2 col-form-label">Exchange Rate</label>
                <div class="col-sm-10">
                  <money
                    v-model="panels.paymentDetailsPanel.data.exchange_rate"
                    placeholder="Enter the amount that was invoiced"
                    v-bind:class="{
                      'has-error':
                        panels.paymentDetailsPanel.errors.exchange_rate,
                    }"
                    class="form-control"
                    v-bind="fieldValues.money"
                  >
                  </money>
                  <small
                    v-if="panels.paymentDetailsPanel.errors.exchange_rate"
                    class="form-text text-danger"
                  >
                    {{
                      panels.paymentDetailsPanel.errors.exchange_rate.join(" ")
                    }}
                  </small>
                </div>
              </div>
            </div>
          </div>

          <input
            type="submit"
            @click="savePaymentDetailsData()"
            :disabled="this.loading"
            v-if="!isReadOnlyUser"
            class="btn btn-block btn-primary"
            value="Add Payment"
          />
        </div>
      </div>
    </div>
    <!-- Payment Details -->

    <!-- Delivery Details -->
    <div id="order-table" class="grid shadow bg-white mt-10">
      <div class="grid-title">
        <h4 class="text-black text-bold">
          <i
            v-if="
              order.fabric_order_deliveries &&
              order.fabric_order_deliveries.length == 0
            "
            class="fa text-success fa-circle-o"
          ></i>
          <i
            v-if="
              order.fabric_order_deliveries &&
              order.fabric_order_deliveries.length > 0
            "
            class="fa text-success fa-check-circle"
          ></i>
          Delivery Details
          <span
            @click="panels.deliveryDetailsPanel.opened = false"
            v-if="panels.deliveryDetailsPanel.opened"
            class="btn btn-primary btn-small pull-right"
          >
            <span class="fa fa-minus-circle"></span>
          </span>

          <span
            @click="panels.deliveryDetailsPanel.opened = true"
            v-if="!panels.deliveryDetailsPanel.opened"
            class="btn btn-primary btn-small pull-right"
          >
            <span class="fa fa-plus-circle"></span>
          </span>
          <router-link
            class="btn btn-primary btn-small pull-right"
            style="margin-right: 10px"
            v-if="!isReadOnlyUser"
            :to="{
              name: 'fabric_order_delivery_create',
              params: { order_id: order.id },
            }"
          >
            Create Delivery</router-link
          >
        </h4>
      </div>
      <div class="grid-body" v-if="panels.deliveryDetailsPanel.opened">
        <template
          v-for="(delivery, deliveryIndex) in order.fabric_order_deliveries"
        >
          <div class="well well-sm" :key="deliveryIndex">
            <div class="row">
              <div class="col-sm-3">
                <h5 class="semi-bold">
                  <a
                    class="delivery-toggle"
                    v-if="delivery.expanded"
                    @click="toggleDeliveryTable(delivery)"
                  >
                    <span class="fa fa-chevron-down"></span>
                  </a>
                  <a
                    class="delivery-toggle"
                    v-if="!delivery.expanded"
                    @click="toggleDeliveryTable(delivery)"
                  >
                    <span class="fa fa-chevron-right"></span>
                  </a>
                  {{ delivery.delivery_reference }}
                  <h5>{{ ezy.date(delivery.created_at) }}</h5>
                </h5>
              </div>
              <div class="col-sm-9 text-right">
                <router-link
                  class="btn btn-primary btn-small"
                  style="margin-right: 5px"
                  v-if="!isReadOnlyUser"
                  :to="{
                    name: 'fabric_order_delivery_update',
                    params: { order_id: order.id, delivery_id: delivery.id },
                  }"
                >
                  Edit Delivery</router-link
                >
                <button
                  class="btn btn-danger btn-small"
                  v-if="!isReadOnlyUser"
                  @click="deleteDelivery(order.id, delivery.id)"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </div>

            <table class="table table-condensed p-0" v-if="delivery.expanded">
              <thead>
                <tr>
                  <th></th>
                  <th>Fabric ID</th>
                  <th>Fabric Name</th>
                  <th>Order Amount</th>
                  <th>Delivery Amount</th>
                  <th>Unit</th>
                  <th>Price/Unit</th>
                  <th>Currency</th>
                  <th>Total Price</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(fabric, fabricIndex) in delivery.fabrics">
                  <tr :key="fabricIndex">
                    <td class="bg-info col-sm-1">
                      <a
                        class="fabric-toggle"
                        v-if="fabric.expanded"
                        @click="toggleFabricRows(fabric)"
                      >
                        <span class="fa fa-chevron-down"></span>
                      </a>
                      <a
                        class="fabric-toggle"
                        v-if="!fabric.expanded"
                        @click="toggleFabricRows(fabric)"
                      >
                        <span class="fa fa-chevron-right"></span>
                      </a>
                    </td>
                    <td class="bg-info">{{ fabric.fabric_id }}</td>
                    <td class="bg-info">{{ fabric.fabric_name }}</td>
                    <td class="bg-info col-sm-2">{{ fabric.order_amount }}</td>
                    <td class="bg-info col-sm-2">
                      {{ fabric.delivery_amount.this_delivery.toFixed(2) }} /
                      {{ fabric.delivery_amount.all_deliveries.toFixed(2) }}
                    </td>
                    <td class="bg-info">{{ fabric.unit }}</td>
                    <td class="bg-info">{{ fabric.unit_price }}</td>
                    <td class="bg-info">{{ fabric.currency }}</td>
                    <td class="bg-info">{{ fabric.total_price }}</td>
                  </tr>
                  <tr
                    v-for="(roll, rollIndex) in fabric.rolls"
                    v-if="fabric.expanded"
                  >
                    <td class="bg-white"></td>
                    <td class="bg-white" colspan="2">#{{ roll.roll_code }}</td>
                    <td class="bg-white col-sm-2">-</td>
                    <td class="bg-white col-sm-2">{{ roll.length }}</td>
                    <td class="bg-white">{{ fabric.unit }}</td>
                    <td class="bg-white">{{ fabric.unit_price }}</td>
                    <td class="bg-white">{{ fabric.currency }}</td>
                    <td class="bg-white">
                      {{ (roll.length * fabric.unit_price).toFixed(2) }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </div>
    <!-- Delivery Details -->

    <!-- Order Details -->
    <div id="order-table" class="grid shadow bg-white mt-10">
      <div class="grid-title">
        <h4 class="text-black text-bold">Fabrics in This Order</h4>
      </div>
      <div class="grid-body">
        <div class="table-responsive">
          <table class="table table-condensed p-0">
            <thead>
              <tr>
                <th class="col-sm-2">Fabric</th>
                <th class="col-sm-1">Order Amount</th>
                <th class="col-sm-1">Unit</th>
                <th class="col-sm-1">Unit Price</th>
                <th class="col-sm-1">VAT</th>
                <th class="col-sm-1">Total Order Price</th>
                <th class="col-sm-1">ETA</th>
                <th class="col-sm-1 text-right">
                  <button
                    class="btn btn-info"
                    @click="addFabric()"
                    v-if="!isReadOnlyUser"
                  >
                    <i class="fa fa-plus-circle"></i> Add Fabric
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(fabric, index) in order.fabrics">
                <tr :key="index">
                  <td>
                    <v-select
                      class="select-fabric"
                      v-bind:class="{
                        'has-error': errors['fabrics.' + index + '.id'],
                      }"
                      :options="fieldValues.fabrics"
                      v-model="fabric.id"
                      label="production_name"
                      :reduce="(fabrics) => fabrics.id"
                      placeholder="Select Fabric"
                      @input="selectFabric(index, fabric)"
                    />
                    <small
                      v-if="errors['fabrics.' + index + '.id']"
                      class="form-text text-danger"
                    >
                      Please choose a fabric
                    </small>
                    <small
                      v-if="errors['fabrics.' + index + '.order_amount']"
                      class="form-text text-danger"
                    >
                      Please enter an amount
                    </small>
                    <small
                      v-if="errors['fabrics.' + index + '.unit']"
                      class="form-text text-danger"
                    >
                      Please enter the correct unit
                    </small>
                    <small
                      v-if="errors['fabrics.' + index + '.unit_price']"
                      class="form-text text-danger"
                    >
                      Please enter a unit price using config
                    </small>
                    <small
                      v-if="errors['fabrics.' + index + '.currency']"
                      class="form-text text-danger"
                    >
                      Please choose a currency
                    </small>
                  </td>
                  <td>
                    <money
                      v-bind:class="{
                        'has-error':
                        errors['fabrics.' + index + '.order_amount'],
                      }"
                      v-model="fabric.order_amount"
                      @input="setTotalPrice(index, fabric)"
                      style="width: 100px"
                      v-bind="fieldValues.money"
                      :disabled="fabric.id === null"
                    />
                  </td>
                  <td>
                    <v-select
                      v-bind:class="{
                        'has-error': errors['fabrics.' + index + '.unit'],
                      }"
                      class="unit"
                      :options="['m', 'y', 'kg']"
                      v-model="fabric.unit"
                      placeholder="Choose Unit"
                      :disabled="fabric.id === null"
                      :clearable="false"
                      @input="updateFooterTotalOrderAmount()"
                    />
                  </td>
                  <td>
                    <money
                      v-bind:class="{
                        'has-error': errors['fabrics.' + index + '.unit_price'],
                      }"
                      v-model="fabric.unit_price"
                      @change="setTotalPrice(index, fabric)"
                      style="width: 100px"
                      :disabled="fabric.id === null"
                        v-bind="fieldValues.money"
                      />
                  </td>
                  <td>
                    <money
                        v-bind:class="{
                          'has-error':
                            errors[
                              'fabrics.' +
                                index +
                                '.defaults.default_vat_per_unit'
                            ],
                        }"
                        v-model="fabric.defaults.default_vat_per_unit"
                        @change="setTotalPrice(index, fabric)"
                        style="width: 100px"
                        :disabled="fabric.id === null"
                        v-bind="fieldValues.money"
                      />
                  </td>
                  <td>
                    <money
                      v-model="fabric.total_price"
                      class="disabled"
                      disabled
                      style="width: 100px"
                      v-bind="fieldValues.money"
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      v-model="fabric.next_order_eta"
                      style="width: 100px"
                      class="next_eta"
                    />
                  </td>
                  <td class="text-right" style="width: 300px">
                    <button
                      class="config btn btn-primary"
                      :disabled="configModal.displayed || fabric.id === null"
                      @click="showConfigurationModal(index)"
                    >
                      <i class="fa fa-gears" aria-hidden="true"></i> Config
                    </button>
                    <button
                      class="btn btn-danger"
                      @click="deleteFabric(index)"
                      v-if="!isReadOnlyUser"
                    >
                      <i class="fa fa-trash" aria-hidden="true"></i>
                    </button>
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot v-if="order.fabrics.length > 0">
              <tr>
                <td class="col-sm-2">{{ getFooterNumberFabrics() }}</td>
                <td class="col-sm-1 text-center">{{ ezy.comma(totalOrderAmount) }}</td>
                <td class="col-sm-1 text-center">{{ getFooterUnit() }}</td>
                <td class="col-sm-1"></td>
                <td class="col-sm-1"></td>
                <td class="col-sm-1 text-center">
                  {{ getFooterTotalPrice() }}
                </td>
                <td class="col-sm-2 text-right">
                  <v-select
                    :options="['THB', 'USD']"
                    :value="getFooterCurrency()"
                    @input="changeCurrency"
                    placeholder="Choose Currency"
                  />
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
    <!-- Order Details -->
    <div class="row">
      <div class="col-md-9">
        <button
          class="btn-primary btn-large btn btn-block"
          :disabled="loading"
          @click="saveData()"
          v-if="!isReadOnlyUser"
        >
          Update Order
        </button>
      </div>
      <div class="col-md-3">
        <button
          class="btn btn-info btn-block btn-large"
          @click="generateOrderText()"
        >
          Show Order Text
        </button>
      </div>
    </div>
    <OrderConfigurationModal
      v-if="configModal.displayed"
      v-on:component-updated="handleModalUpdate"
      v-on:hide-modal="hideConfigurationModal"
      :fieldValues="fieldValues"
      :modelValues="order.fabrics[configModal.fabricIndex]"
      :isReadOnlyUser="isReadOnlyUser"
      :errors="errors"
    >
    </OrderConfigurationModal>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import { Money } from "v-money";
import alasql from "alasql";
import app from "../Tools/Fabric";
import OrderConfigurationModal from "./components/OrderConfigurationModal";
import OrderNotes from '../Tools/OrderNote.vue';

export default {
  components: {
    OrderConfigurationModal,
    Datepicker,
    Money,
    OrderNotes
  },
  directives: { money: Money },
  watch: {
    "order.invoice_net": {
      async handler(newVal) {
        this.order.invoice_total = (
          parseFloat(newVal) + parseFloat(this.order.invoice_vat)
        ).toFixed(2);
      },
    },
    "order.invoice_vat": {
      async handler(newVal) {
        this.order.invoice_total = (
          parseFloat(newVal) + parseFloat(this.order.invoice_net)
        ).toFixed(2);
      },
    },
  },
  data() {
    return {
      errors: [],
      isReadOnlyUser: false,
      panels: {
        invoiceDetailsPanel: {
          opened: false,
        },
        paymentDetailsPanel: {
          opened: false,
          errors: [],
          data: {
            type: null,
            amount_invoiced: null,
            amount_other: null,
            currency_invoiced: null,
            currency_paid: null,
            exchange_rate: null,
          },
          results: [],
          addPaymentDialogOpened: false,
        },
        deliveryDetailsPanel: {
          opened: false,
        },
        fabricNotesPanel: {
          opened: false
        }
      },
      fieldValues: {
        weaves: [],
        fibers: [],
        finishes: [],
        patterns: [],
        coloringStyles: [],
        fabricTypes: [],
        suppliers: [],
        buttonColors: [],
        threadColors: [],
        embroideryThreadColors: [],
        allFabrics: [],
        fabrics: [],
        interlinings: [],
        interliningColors: [],
        money: {
          decimal: ".",
          thousands: ",",
          prefix: "",
          suffix: "",
          precision: 2,
          masked: false,
        },
      },
      order: {
        order_reference: "",
        fabric_supplier_id: null,
        fabrics: [],
      },
      configModal: {
        displayed: false,
        fabricIndex: null,
      },
      loading: false,
      oldConfigValues: {
        type: [Object],
        default: null,
      },
      totalOrderAmount: 0,
    };
  },
  async mounted() {
    this.isReadOnlyUser = this.$auth.user().role === "read_only";
    let orderId = this.$route.params.order_id;
    await this.getPageData(orderId);
    await this.getPaymentDetailsData();
    this.updateFooterTotalOrderAmount();
  },
  methods: {
    generateOrderText() {
      let fabrics = this.order.fabrics;

      let html = `<div class="text-left"> Dear ${this.order.supplier.contact_person_title}${this.order.supplier.contact_person_name}<br>
            I would like to place the following order:<br><br>`;

      fabrics.forEach((fabric) => {
        html += `<span>
                - ${fabric.order_amount} ${fabric.unit} of
                ( ${fabric.defaults.fabric.name} - ${fabric.defaults.fabric.current_fabric_supplier_code})
                </span><br>`;
      });

      html += `<br>Could you give me an ETA for those fabrics?<br>
            Best regards,
            Befeni</div><br>`;

      Swal.fire({
        title: "Message to Supplier",
        html: html,
        showCloseButton: true,
        showCancelButton: true,
        showConfirmButton: false,
      });
    },

    /**
     * Invoice Details Panel Methods
     */
    selectInvoiceDate() {
      this.order.invoice_date = moment(this.order.invoice_date_picked).format(
        "YYYY-MM-DD"
      );
    },

    async saveInvoiceData() {
      this.errors = [];
      this.loading = true;
      try {
        let orderId = this.$route.params.order_id;
        let results = await axios({
          method: "post",
          url: `/v2/fabrics/orders/${orderId}/invoice`,
          data: this.order,
        });
        Swal.fire({
          title: "Invoice details saved.",
          type: "success",
        }).then((result) => {
          this.loading = false;
        });
      } catch (error) {
        this.loading = false;
        if (error.response.status === 403) {
          toastr.error(
            "Not authorized, please ensure that you have the required permissions."
          );
        } else {
          this.errors = error.response.data.errors;
          toastr.error(
            "There was some errors, please correct and try again, or contact the admins"
          );
        }
      }
    },

    /**
     * Payment Details Panel
     */

    changeOrderPaymentStatus(status) {
      this.order.payment_status = status;
      this.saveData();
    },

    resetAddPaymentForm() {
      let panel = this.panels.paymentDetailsPanel;
      panel.addPaymentDialogOpened = false;
      panel.data = {
        type: null,
        amount_invoiced: null,
        amount_other: null,
        currency_invoiced: null,
        currency_paid: null,
        exchange_rate: null,
      };
    },

    async getPaymentDetailsData() {
      let orderId = this.$route.params.order_id;
      try {
        let results = await axios({
          method: "get",
          url: `/v2/fabrics/orders/${orderId}/payment`,
        });
        this.panels.paymentDetailsPanel.results = results.data.data.payments;
        this.order.payment_status = results.data.data.payment_status;
      } catch (error) {
        if (error.response.status === 403) {
          toastr.error(
            "Not authorized, please ensure that you have the required permissions."
          );
        } else {
          toastr.error(
            "There was some errors, please correct and try again, or contact the admins"
          );
        }
      }
    },

    async savePaymentDetailsData() {
      let panel = this.panels.paymentDetailsPanel;
      let orderId = this.$route.params.order_id;
      panel.errors = [];
      this.loading = true;
      try {
        // the v-money plugin forces exchange rate to 0 instead of null so we need this hack
        // to prevent the backend api's validation money from crying about it...
        panel.data.exchange_rate =
          panel.data.exchange_rate === 0 ? null : panel.data.exchange_rate;

        let results = await axios({
          method: "post",
          url: `/v2/fabrics/orders/${orderId}/payment`,
          data: panel.data,
        });
        Swal.fire({
          title: "Payment detail saved.",
          type: "success",
        }).then((result) => {
          this.resetAddPaymentForm();
          this.getPaymentDetailsData();
          this.loading = false;
        });
      } catch (error) {
        this.loading = false;
        if (error.response.status === 403) {
          toastr.error(
            "Not authorized, please ensure that you have the required permissions."
          );
        } else {
          panel.errors = error.response.data.errors;
          toastr.error(
            "There was some errors, please correct and try again, or contact the admins"
          );
        }
      }
    },

    async deletePayment(id) {
      let panel = this.panels.paymentDetailsPanel;
      let orderId = this.$route.params.order_id;
      this.loading = true;
      try {
        let results = await axios({
          method: "delete",
          url: `/v2/fabrics/orders/${orderId}/payment/${id}`,
        });
        Swal.fire({
          title: "Payment detail deleted.",
          type: "success",
        }).then((result) => {
          this.getPaymentDetailsData();
          this.loading = false;
        });
      } catch (error) {
        this.loading = false;
        if (error.response.status === 403) {
          toastr.error(
            "Not authorized, please ensure that you have the required permissions."
          );
        } else {
          toastr.error("Error deleting Payment");
        }
      }
    },

    /**
     * Delivery Details Panel
     */

    toggleDeliveryTable(delivery) {
      let currentStatus = delivery.expanded;
      delivery.expanded = !currentStatus;
    },

    toggleFabricRows(fabric) {
      let currentStatus = fabric.expanded;
      fabric.expanded = !currentStatus;
    },

    async deleteDelivery(orderId, deliveryId) {
      let res = await Swal.fire({
        title: "Are you sure you want to delete this delivery?",
        html: "<span class='text-danger'>If any rolls in the delivery have been withdrawn from the warehouse, this operation will fail!</span>",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Delete Delivery",
      });

      if (res.value) {
        try {
          await axios({
            method: "delete",
            url: `/v2/fabrics/order/${orderId}/delivery/${deliveryId}/delete`,
          });
          Swal.fire("Success!", "Delivery deleted!", "success");
          // @todo this is lazy, let's make a method just
          // for updating deliveries!!!
          this.getPageData(orderId);
        } catch (error) {
          if (error.response.status === 403) {
            toastr.error(
              "Not authorized, please ensure that you have the required permissions."
            );
          } else {
            Swal.fire("Error!", "Could not delete Delivery!", "error");
          }
        }
      }
    },

    /**
     * Base Order Methods
     */
    async getPageData(orderId) {
      try {
        let response = await axios({
          method: "get",
          url: `/v2/fabrics/orders/${orderId}/edit`,
        });

        let data = response.data.data;

        this.order = data.order;
        this.order.invoice_date_picked = this.order.invoice_date
        this.order.fabrics = this.order.fabrics.map((fabric, index) => {
          fabric.default_unit = fabric.unit;
          fabric.order_amount = fabric.order_amount.toFixed(2);
          fabric.total_price = fabric.total_price.toFixed(2);
          return fabric
        });

        // load the field values
        this.fieldValues.weaves = data.weaves;
        this.fieldValues.fibers = data.fibers;
        this.fieldValues.finishes = data.finishes;
        this.fieldValues.patterns = data.patterns;
        this.fieldValues.coloringStyles = data.coloringStyles;
        this.fieldValues.fabricTypes = data.fabricTypes;
        this.fieldValues.buttonColors = data.buttonColors;
        this.fieldValues.threadColors = data.threadColors;
        this.fieldValues.embroideryThreadColors = data.embroideryThreadColors;
        this.fieldValues.suppliers = data.suppliers;
        this.fieldValues.fabrics = data.fabrics;
        this.fieldValues.allFabrics = data.fabrics;
        this.fieldValues.compositionLabels = data.compositionLabels;
        this.fieldValues.certificates = data.certificates;
        this.fieldValues.formattedCompositionLabels =
          app.getFormattedCompositionLabels(data.compositionLabels);
      } catch (error) {
        if (error.response.status === 403) {
          toastr.error(
            "Not authorized, please ensure that you have the required permissions."
          );
        } else {
          toastr.error(
            "There was some errors, please correct and try again, or contact the admins"
          );
        }
      }
    },

    async saveData() {
      let resp = alasql("SELECT count(*) as total, id FROM ? GROUP BY id", [
        this.order.fabrics,
      ]);
      let res = alasql("SELECT * from ? where total > 1", [resp]);
      if (res.length > 0) {
        Swal.fire({
          title: "Duplicate Fabric Exist!",
          type: "error",
        });
        return false;
      }

      this.errors = [];
      this.loading = true;
      try {
        let mainCurrency = this.getFooterCurrency();
        this.changeCurrency(mainCurrency);

        let orderId = this.$route.params.order_id;
        let results = await axios({
          method: "put",
          url: `/v2/fabrics/orders/${orderId}`,
          data: this.order,
        });
        Swal.fire({
          title: "Order details saved.",
          type: "success",
        }).then((result) => {
          this.loading = false;
        });
      } catch (error) {
        this.loading = false;
        if (error.response.status === 403) {
          toastr.error(
            "Not authorized, please ensure that you have the required permissions."
          );
        } else if (error.response.status === 422) {
          toastr.error(error.response.data.errors.join(','));
        } else {
          this.errors = error.response.data.errors;
          toastr.error(
            "There was some errors, please correct and try again, or contact the admins"
          );
        }
      }
    },

    addFabric(fabricId) {
      let defaults = this.order.fabrics.length > 0 ? {...this.order.fabrics[this.order.fabrics.length - 1].defaults} : {}
      let newIndex =
        this.order.fabrics.push({
          id: fabricId,
          order_amount: 0,
          unit: "m",
          unit_price: 0,
          currency: "THB",
          total_price: 0,
          next_order_eta: null,
          overrides: {
            default_width: null,
            default_unit: null,
            default_unit_price: null,
            default_vat: null,
            default_unit_price_total: null,
            current_fabric_supplier_code: null,
            fiber_1: null,
            fiber_1_percentage: null,
            fiber_2: null,
            fiber_2_percentage: null,
            fiber_3: null,
            fiber_3_percentage: null,
            fabric_composition_label_id: null,
            default_yarn_count: null,
            default_density: null,
            default_weight: null,
            default_coloring_style: null,
            default_finish: null,
          },
          defaults
        }) - 1;

      if (fabricId) {
        this.selectFabric(newIndex, this.order.fabrics[newIndex]);
      }
    },

    // @todo MOAR work needed
    deleteFabric(orderFabricIndex) {
      this.order.fabrics.splice(orderFabricIndex, 1);
    },

    async selectFabric(orderFabricIndex, fabric) {
      try {
        let res = await axios.get(
          `v2/fabrics/${fabric.id}/configuration/last_supplier_order?supplier_id=${this.order.supplier.id}`
        );

        let config = res.data.data.config;

        // add the default values onto the fabric object in the order

        fabric.defaults = config;
        fabric.order_amount = 0;
        fabric.unit = config.default_unit;
        fabric.unit_price = config.default_unit_price;
        fabric.currency = config.default_currency;

        this.setTotalPrice(orderFabricIndex, fabric);
      } catch (error) {
        if (error.response.status === 403) {
          toastr.error(
            "Not authorized, please ensure that you have the required permissions."
          );
        } else {
          toastr.error(
            "There was some errors, please correct and try again, or contact the admins"
          );
        }
      }
    },

    showConfigurationModal(orderFabricIndex) {
      this.configModal = {
        displayed: true,
        fabricIndex: orderFabricIndex,
      };

      // Make a deep copy
      this.oldConfigValues = JSON.parse(
        JSON.stringify(this.order.fabrics[orderFabricIndex])
      );

      setTimeout(function () {
        // hack: needs to be in a setTimeout to give the DOM enough
        // time to init the modal because ... jquery!
        show_modal("#configuration-modal");
      }, 250);
    },

    hideConfigurationModal(revertOldValues = true) {
      if (revertOldValues) {
        // Reset back fabric configuration to the old values
        for (var key in this.oldConfigValues) {
          this.order.fabrics[this.configModal.fabricIndex][key] =
            this.oldConfigValues[key];
        }
      }

      this.configModal = {
        displayed: false,
        fabricIndex: null,
      };

      hide_modal("#configuration-modal");
    },

    /**
     * When the modal emits an 'component-updated'
     * event, it is handled by this method
     */
    handleModalUpdate(event, fabric) {
      let fabricDetails = this.order.fabrics[this.configModal.fabricIndex];

      fabricDetails.overrides = { ...fabricDetails.overrides, ...event };

      let allowUpdateOrder = true;

      if (this.$auth.user().role === "admin") {
        const totalFiberPercentage =
          parseInt(fabricDetails.overrides.fiber_1_percentage || 0) +
          parseInt(fabricDetails.overrides.fiber_2_percentage || 0) +
          parseInt(fabricDetails.overrides.fiber_3_percentage || 0);

        if (totalFiberPercentage !== 100) {
          allowUpdateOrder = false;
          toastr.error("Total of fiber percentage must be equal to 100");
        }
      }

      if (allowUpdateOrder) {
        // trigger total price change if the values above have changed
        this.setTotalPrice(this.configModal.fabricIndex, fabricDetails);
        this.hideConfigurationModal(false);
      }
    },

    setTotalPrice(orderFabricIndex, fabric) {
      fabric.overrides.default_unit_price = fabric.unit_price;
      fabric.overrides.default_vat_per_unit =
        fabric.defaults.default_vat_per_unit;
      fabric.total_price = (
        (parseFloat(fabric.unit_price) +
          parseFloat(fabric.defaults.default_vat_per_unit)) *
        fabric.order_amount
      ).toFixed(2);
      fabric.defaults.default_unit_price_total =
        parseFloat(fabric.unit_price) +
        parseFloat(fabric.defaults.default_vat_per_unit);
      fabric.overrides.default_unit_price_total =
        fabric.defaults.default_unit_price_total;
      this.updateFooterTotalOrderAmount();
    },

    /* Get Footer Details */

    getFooterNumberFabrics() {
      return this.order.fabrics.length > 0
        ? "Fabrics (" + this.order.fabrics.length + ")"
        : "-";
    },

    updateFooterTotalOrderAmount() {
      let totalOrderAmount = 0;
      if (this.order.fabrics.length > 0) {
        const unit = this.getFooterUnit();
        totalOrderAmount = this.order.fabrics.reduce((total, fabric) => {
          let subTotal = 0;
          if (unit === "m") {
            if(fabric.unit === "y") {
              subTotal = parseFloat(fabric.order_amount) * 0.9144;
            } else if (fabric.unit === "kg") {
              subTotal = this.getJerseyLengthEstimationInMeter(fabric);
            } else {
              subTotal = parseFloat(fabric.order_amount);
            }
          }
          if (unit === "y") {
            if(fabric.unit === "m") {
              subTotal = parseFloat(fabric.order_amount) * 1.094;
            } else if (fabric.unit === "kg") {
              subTotal = this.getJerseyLengthEstimationInMeter(fabric) * 1.094; // Convert the m to y
            } else {
              subTotal = parseFloat(fabric.order_amount);
            }
          }
          if (unit === "kg") {
            return total += parseFloat(fabric.order_amount);
          }

          return (total += subTotal);
        }, 0);
      }
      this.totalOrderAmount = totalOrderAmount.toFixed(2);
    },
    /**
     * Always display m or y, unless there are only Jersey fabrics in the list, in that case display kg
     */
    getFooterUnit() {

      if(this.order.fabrics.length === 0) {
        return '-';
      }
      let firstNonJerseyFabric = this.order.fabrics.find(fabric => fabric.is_jersey_fabric === 0);
      return firstNonJerseyFabric ? firstNonJerseyFabric.unit : 'kg';
    },

    getFooterCurrency() {
      return this.order.fabrics.length > 0
        ? this.order.fabrics[0].currency
        : null;
    },

    changeCurrency(value) {
      this.order.fabrics = this.order.fabrics.map((f) => {
        f.currency = value;
        return f;
      });
    },

    getFooterTotalPrice() {
      let totalPrice = 0;
      if (this.order.fabrics.length > 0) {
        totalPrice = this.order.fabrics.reduce(function (total, fabric) {
          return (total += parseFloat(fabric.total_price));
        }, 0);
      }
      return this.ezy.comma(totalPrice);
    },

    getJerseyLengthEstimationInMeter(fabric) {
      let defaultWeight = parseFloat(fabric.default_weight);
      let meterPerKg = 0;
      if(fabric.knit_type === 'open') {
        meterPerKg = 1000 / ((parseFloat(fabric.default_width) + 2) * defaultWeight * 0.0232);
      } else {
        meterPerKg = 1000 / (parseFloat(fabric.default_width) * 2 * defaultWeight * 0.0232);
      }
      return fabric.order_amount * meterPerKg;
    }
  },
};
</script>

<style scoped>
@import "~sweetalert2/dist/sweetalert2.min.css";
@import "~vue-select/dist/vue-select.css";

.grid-title h4 {
  width: 100%;
}

.grid-body {
  padding-bottom: 0px !important;
}

/** Order Reference */
div#order-reference-container h5 {
  margin-bottom: 5px !important;
}

div#order-reference-container h1 {
  margin-top: 0px !important;
}

/** Supplier Details */
div#supplier-container h5 {
  margin-bottom: 5px !important;
}

div#supplier-container h1 {
  margin-top: 0px !important;
}

/* Payment Details */

#order-payment-status-container {
  background: #eee;
  padding: 10px 20px;
  border: 1px solid #ddd;
  margin-bottom: 20px;
}

#add-payment-form-container {
  padding: 20px;
  border: 2px solid #eaeaea;
  padding-bottom: 20px;
  margin-bottom: 30px;
}

#add-payment-form-container h5 {
  font-size: 18px;
  padding-bottom: 10px;
}

/** Order Table */
div#order-table table th {
  border: 0px !important;
}

div#order-table table tr td {
  vertical-align: top;
}

div#order-table table thead th {
  vertical-align: middle;
}

div#order-table table tfoot tr td {
  border-top: 2px solid #e5e5e5;
  font-weight: bold;
}

/** Invoice Details Panel */

.minimized {
  height: 0px !important;
  overflow: hidden;
  padding-bottom: 0px !important;
}

.mb-20 {
  margin-bottom: 20px;
}

.col-form-label {
  margin-top: 10px;
}

a.delivery-toggle,
a.fabric-toggle {
  cursor: pointer;
}

/** Form Error States */

input.has-error,
select.has-error,
.v-select.has-error {
  border: 1px solid #a94442 !important;
}

.disabled {
  background: #ececec;
  cursor: not-allowed;
}

.unit {
  width: 150px !important;
}
.next_eta {
  width: 150px !important;
}

.select-fabric {
  width: 250px;
}

small.form-text {
  display: block;
}
div#order-table table tfoot tr td {
  vertical-align: middle;
}
</style>
